/**
 * Pavage de logos sur un modele tubulaire (tour de cou).
 *
 * Le gabarit fournisseur (ref DA3) ne montre pas une frise mais un motif plein :
 * le logo couvre toute la surface, en rangs horizontaux dont chacun est decale
 * par rapport au precedent, ce qui fait apparaitre des diagonales. Le logo est
 * en outre legerement incline.
 *
 * Tout se calcule en centimetres reels, jamais en UV : l'UV du tour de cou est
 * anisotrope (le tour et la hauteur n'ont pas la meme echelle), et raisonner en
 * UV donnerait des logos deformes et des espacements faux.
 *
 * Deux contraintes structurent le calcul :
 *   - le nombre de colonnes est un ENTIER, sinon le motif ne se referme pas a
 *     la couture u=0/u=1 et laisse un raccord visible ;
 *   - les rangs, eux, debordent volontairement en haut et en bas : les logos
 *     coupes aux bords sont exactement ce que montre le gabarit.
 */

import type { PrintAreaCm } from './uvTiling';

export type LogoPatternConfig = {
  /** Largeur d'un logo, en cm. */
  sizeCm: number;
  /** Inclinaison de chaque logo, en degres. */
  angleDeg: number;
  /** Espacement horizontal souhaite (autour du tour), en cm. */
  spacingCm: number;
  /** Espacement vertical entre deux rangs, en cm. */
  rowSpacingCm: number;
  /**
   * Decalage applique a chaque rang par rapport au precedent, en fraction de
   * l'espacement horizontal. 0 = rangs alignes, 0.5 = quinconce, les valeurs
   * intermediaires produisent des diagonales plus ou moins inclinees.
   */
  rowOffsetRatio: number;
};

export type LogoPatternInstance = {
  /** Centre du logo, en UV (0-1). u est toujours ramene dans [0,1[. */
  u: number;
  v: number;
};

export type LogoPatternLayout = {
  /** Nombre de colonnes sur le tour (entier : le motif se referme). */
  columns: number;
  /** Nombre de rangs dessines, debordement compris. */
  rows: number;
  /** Espacement horizontal reellement applique, en cm. */
  actualSpacingCm: number;
  /** Espacement vertical applique, en cm. */
  rowSpacingCm: number;
  /** Largeur d'un logo apres bridage, en cm. */
  logoWidthCm: number;
  /** Hauteur d'un logo, deduite du ratio de l'image, en cm. */
  logoHeightCm: number;
  /** Inclinaison appliquee, en degres. */
  angleDeg: number;
  /** Nombre total de logos dessines. */
  total: number;
  instances: LogoPatternInstance[];
};

/** Garde-fous : au-dela le rendu coute cher pour un gain visuel nul. */
export const PATTERN_MAX_COLUMNS = 40;
export const PATTERN_MAX_ROWS = 40;

export const LOGO_PATTERN_DEFAULTS: LogoPatternConfig = {
  sizeCm: 6,
  angleDeg: 10,
  spacingCm: 7,
  rowSpacingCm: 6,
  rowOffsetRatio: 0.5,
};

/**
 * Dispose les logos sur toute la zone imprimable.
 *
 * @param logoAspect hauteur/largeur de l'image du logo.
 */
export function computeLogoPatternLayout(
  area: PrintAreaCm,
  config: LogoPatternConfig,
  logoAspect: number
): LogoPatternLayout {
  const widthCm = Math.max(0.1, area.widthCm);
  const heightCm = Math.max(0.1, area.heightCm);
  const aspect = logoAspect > 0 && Number.isFinite(logoAspect) ? logoAspect : 1;

  // Un logo ne peut pas depasser la moitie du tour, sinon il se recouvre
  // lui-meme en faisant le tour.
  const sizeCm = Math.min(Math.max(0.1, config.sizeCm), widthCm / 2);
  const logoHeightCm = sizeCm * aspect;

  // Colonnes : entier, donc le motif se referme a la couture.
  const columns = Math.min(
    PATTERN_MAX_COLUMNS,
    Math.max(1, Math.round(widthCm / Math.max(0.1, config.spacingCm)))
  );
  const actualSpacingCm = widthCm / columns;

  // Rangs : on couvre la hauteur et on ajoute un rang de marge de chaque cote,
  // pour que les logos soient coupes aux bords au lieu de s'arreter avant.
  const rowSpacingCm = Math.max(0.1, config.rowSpacingCm);
  const rows = Math.min(
    PATTERN_MAX_ROWS,
    Math.max(1, Math.ceil(heightCm / rowSpacingCm) + 2)
  );

  const offsetRatio = Number.isFinite(config.rowOffsetRatio) ? config.rowOffsetRatio : 0;

  const instances: LogoPatternInstance[] = [];
  for (let row = 0; row < rows; row += 1) {
    // Rangs centres sur la bande, debordement symetrique en haut et en bas.
    const yCm = (row - (rows - 1) / 2) * rowSpacingCm + heightCm / 2;
    const v = yCm / heightCm;
    // Decalage cumulatif : c'est lui qui cree les diagonales du gabarit.
    const shift = (((row * offsetRatio) % 1) + 1) % 1;
    for (let col = 0; col < columns; col += 1) {
      const u = (((col + shift) / columns) % 1 + 1) % 1;
      instances.push({ u, v });
    }
  }

  return {
    columns,
    rows,
    actualSpacingCm,
    rowSpacingCm,
    logoWidthCm: sizeCm,
    logoHeightCm,
    angleDeg: config.angleDeg,
    total: instances.length,
    instances,
  };
}
